<template>
	<div class="ref-header-row">
		<div class="ref-header-icon"><sym-icon :name="row.icon"></sym-icon></div>
		<div class="ref-header-block">
			<div class="ref-header-main-row">
					<span class="ref-header-titles">
						<it-link v-if="row.link" class="ref-header-title" :to="row.link">{{ row.title }}</it-link>
						<object-link v-else-if="row.srcType && row.srcId" :src-id="row.srcId" :src-type="row.srcType" :src-sub-type="row.srcSubType">{{ row.title }}</object-link>
						<span v-else class="ref-header-title">{{ row.title }}</span>
						<span v-if="row.subtitle" class="ref-header-subtitle">{{ row.subtitle }}</span>
					</span>
					<span v-if="hasButtons" class="ref-header-buttons">
						<template v-for="b of row.buttons">
							<a v-if="b.blank" :href="b.link" target="_blank"><sym-icon :name="b.icon"></sym-icon></a>
							<it-link v-else :to="b.link"><sym-icon :name="b.icon"></sym-icon></it-link>
						</template>
						<span><icon-button v-if="edit" name="edit" @click="emit('edit')"></icon-button></span>
					</span>
			</div>
			<div class="ref-header-info">
				<template v-if="hasPhones">
					<span class="ref-info-pair"><span class="ref-info-label">{{ env.msg('customer.phones') }}:</span>
					<ea-list :list="phones" tag="span" class="ref-info-value"></ea-list></span>
				</template>
				<template v-if="hasEmails">
					<span class="ref-info-pair"><span class="ref-info-label">{{ env.msg('customer.email') }}:</span>
						<ea-list :list="emails" tag="span" class="ref-info-value"></ea-list></span>
				</template>
			</div>
			<div v-if="hasInfos" class="ref-header-info">
				<span v-for="info of row.info" class="ref-info-pair">
					<span v-if="info.label" class="ref-info-label">{{ info.label }}:</span>
					<span v-html="info.html" class="ref-info-value"></span>
				</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import ObjectLink from "@fd/helpers/ObjectLink.vue";
import IconButton from "@fd/helpers/IconButton.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import ItLink from "@/router/ItLink.vue";
import type {HeaderRow} from "@t/fd/editor/References.ts";
import {EAddress, EaType} from "@t/customer/EAddress.ts";
import {injectEnv} from "@fd/Environment.ts";
import EaList from "@fd/editors/member/EaList.vue";

const props = withDefaults(defineProps<{
	row: HeaderRow,
	edit: boolean
}>(), { edit: false });
const env = injectEnv();
const emit = defineEmits(['edit']);

const eaddresses = computed<EAddress[]>(() => {
	return props.row?.eaddresses ?? [];
});

const phones = computed<EAddress[]>(() => {
	return eaddresses.value.filter((ea) => ea.type === EaType.PHONE);
});
const hasPhones = computed<boolean>(() => phones.value.length > 0);

const emails = computed<EAddress[]>(() => {
	return eaddresses.value.filter((ea) => ea.type === EaType.EMAIL);
});
const hasEmails = computed<boolean>(() => emails.value.length > 0);
const hasInfos = computed<boolean>(() => Array.isArray(props.row.info) && props.row.info.length > 0);

const hasButtons = computed<boolean>(() => {
	return props.edit || (Array.isArray(props.row.buttons) && props.row.buttons.length > 0);
});
</script>

<style>
.ref-header-row {
	display: flex;
	flex-direction: row;
	column-gap: 4px;
}

.ref-header-icon {
	text-align: center;
	width: 20px;
}
.ref-header-block {
	flex: 1 1 auto;
}

.ref-header-main-row {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	column-gap: 16px;
	justify-content: space-between;
}

.ref-header-titles {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
}
.ref-header-title {
	font-weight: 500;
}
.ref-header-subtitle {
	font-size: var(--font-size-s);
	opacity: 0.5;
}
.ref-header-buttons {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
	font-size: var(--font-size);
}
.ref-header-info {
	font-size: var(--font-size-s);
}
.ref-info-label {
	font-weight: 600;
}
.ref-header-info {
	display: block;
	line-height: 1.5em;
}
.ref-info-pair {
	display: inline;

	&:not(:first-child)::before {
		content: ' | ';
		opacity: 0.5;
	}
}
.ref-info-label {
	margin-right: 4px;
}
</style>